import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { usersAPI } from '../../api';
import { apiError, cleanUsername, parseLimit, passwordProblem, usernameProblem } from './usersUtils';

const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const inputClass =
  'block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-blue-500';
const hintClass = 'mt-1 text-sm text-gray-500';
const errorClass = 'mt-1 text-sm text-red-600';

const emptyForm = (roles) => ({
  full_name: '',
  username: '',
  password: '',
  email: '',
  role_id: (roles.find((r) => r.code === 'cashier') || roles.find((r) => !r.is_admin) || roles[0])?.id ?? '',
  limitMode: 'role',
  ownLimit: '',
});

const formFor = (person) => ({
  full_name: person.full_name || '',
  username: person.username,
  password: '',
  email: person.email || '',
  role_id: person.role?.id ?? '',
  limitMode: person.discount_limit === null || person.discount_limit === undefined ? 'role' : 'own',
  ownLimit: person.discount_limit ?? '',
});

/**
 * Add a person (person = null) or edit one. Saving calls onSaved with the server's copy.
 */
const UserFormModal = ({ isOpen, onClose, person, roles, isSelf, onSaved }) => {
  const editing = Boolean(person);
  const [form, setForm] = useState(() => emptyForm(roles));
  const [showPassword, setShowPassword] = useState(false);
  const [problems, setProblems] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(person ? formFor(person) : emptyForm(roles));
    setProblems({});
    setServerError('');
    setShowPassword(false);
  }, [isOpen, person, roles]);

  const role = useMemo(() => roles.find((r) => r.id === Number(form.role_id)), [roles, form.role_id]);
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const renamed = editing && cleanUsername(form.username) !== (person.username || '').toLowerCase();

  const check = () => {
    const found = {};
    if (!form.full_name.trim()) found.full_name = "Type the person's name.";
    const nameProblem = usernameProblem(form.username);
    if (nameProblem) found.username = nameProblem;
    if (!editing) {
      const pwProblem = passwordProblem(form.password);
      if (pwProblem) found.password = pwProblem;
    }
    if (!role) found.role_id = 'Pick a role.';
    if (form.limitMode === 'own' && parseLimit(form.ownLimit) === null) {
      found.ownLimit = 'Type a whole number from 0 to 100.';
    }
    setProblems(found);
    return Object.keys(found).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !check()) return;
    const payload = {
      full_name: form.full_name.trim(),
      username: cleanUsername(form.username),
      role_id: Number(form.role_id),
      discount_limit: form.limitMode === 'own' ? parseLimit(form.ownLimit) : null,
    };
    const email = form.email.trim();
    if (email) payload.email = email;
    setSaving(true);
    setServerError('');
    try {
      const response = editing
        ? await usersAPI.update(person.id, payload)
        : await usersAPI.create({ ...payload, password: form.password });
      onSaved(response.data);
    } catch (error) {
      setServerError(apiError(error, 'Could not save. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editing ? `Edit ${person.full_name || person.username}` : 'Add a person'} size="md">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {serverError && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {serverError}
          </div>
        )}

        <div>
          <label htmlFor="user-full-name" className={labelClass}>Name</label>
          <input
            id="user-full-name"
            autoFocus
            value={form.full_name}
            onChange={set('full_name')}
            className={inputClass}
            placeholder="e.g. Ali Raza"
            maxLength={100}
          />
          {problems.full_name && <p className={errorClass}>{problems.full_name}</p>}
        </div>

        <div>
          <label htmlFor="user-username" className={labelClass}>Username</label>
          <input
            id="user-username"
            value={form.username}
            onChange={set('username')}
            className={inputClass}
            placeholder="e.g. ali"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={50}
          />
          {problems.username ? (
            <p className={errorClass}>{problems.username}</p>
          ) : renamed ? (
            <p className="mt-1 text-sm text-amber-700">
              They will sign in with the new username. If they are signed in now, they will be signed out.
            </p>
          ) : (
            <p className={hintClass}>They type this to sign in. Small letters, no spaces.</p>
          )}
        </div>

        {!editing && (
          <div>
            <label htmlFor="user-password" className={labelClass}>Password</label>
            <div className="relative">
              <input
                id="user-password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={set('password')}
                className={`${inputClass} pr-10`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {problems.password ? (
              <p className={errorClass}>{problems.password}</p>
            ) : (
              <p className={hintClass}>At least 6 characters. Tell the person their password.</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="user-role" className={labelClass}>Role</label>
          <select
            id="user-role"
            value={form.role_id}
            onChange={set('role_id')}
            disabled={isSelf}
            className={`${inputClass} disabled:bg-gray-100 disabled:text-gray-500`}
          >
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {problems.role_id && <p className={errorClass}>{problems.role_id}</p>}
          {isSelf ? (
            <p className={hintClass}>You can&apos;t change your own role. Another admin can.</p>
          ) : (
            role?.description && <p className={hintClass}>{role.description}</p>
          )}
        </div>

        <fieldset>
          <legend className={labelClass}>Discount limit</legend>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="limit-mode"
                checked={form.limitMode === 'role'}
                onChange={() => setForm((f) => ({ ...f, limitMode: 'role' }))}
                className="text-blue-600 focus:ring-blue-500"
              />
              Use the role&apos;s limit{role ? ` (${role.discount_limit}%)` : ''}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name="limit-mode"
                checked={form.limitMode === 'own'}
                onChange={() => setForm((f) => ({ ...f, limitMode: 'own' }))}
                className="text-blue-600 focus:ring-blue-500"
              />
              Own limit
              <input
                type="number"
                min="0"
                max="100"
                inputMode="numeric"
                value={form.ownLimit}
                onChange={(e) => setForm((f) => ({ ...f, limitMode: 'own', ownLimit: e.target.value }))}
                className="w-20 rounded-lg border border-gray-300 px-2 py-1 focus:border-blue-500 focus:ring-blue-500"
                aria-label="Own discount limit in percent"
              />
              %
            </label>
          </div>
          {problems.ownLimit ? (
            <p className={errorClass}>{problems.ownLimit}</p>
          ) : (
            <p className={hintClass}>A bigger discount at the till needs an OTP code.</p>
          )}
        </fieldset>

        <div>
          <label htmlFor="user-email" className={labelClass}>
            Email <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input
            id="user-email"
            type="email"
            value={form.email}
            onChange={set('email')}
            className={inputClass}
            autoCapitalize="none"
            maxLength={100}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save' : 'Add person'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserFormModal;
